// Lecturas y escrituras de la sección Cables. Mismas reglas que
// arbolesService: los errores se LANZAN con mensaje para la persona (nunca se
// vuelven "sin datos"), y si la tabla todavía no existe en la base, el mensaje
// lo dice ("falta aplicar la actualización").
//
// - cables_lineas: ficha. Admin y editor crean y editan; nadie borra, admin
//   da de baja con motivo.
// - cables_linea_eventos: append-only; admin anula con motivo.
// - cables_lineas_historial: la base guarda la fila anterior en cada cambio.
// - cables_v_lineas_estado: cable + códigos de sus árboles + último evento
//   vigente + resultado vigente + vida útil + menciones de 30 días.
// - Archivos en el bucket cables-evidencia, carpeta lineas/<linea_id>/.
import { supabase } from 'lib/supabase';
import { compressImage } from 'lib/compressImage';
import {
  BUCKET, errorAmigable, leer, leerPaginado, exigirMotivo, anular,
} from './adminService';
import { validarInformePdf } from './arbolesService';

const LINEAS = 'cables_lineas';
const EVENTOS = 'cables_linea_eventos';
const HIST = 'cables_lineas_historial';
const VISTA = 'cables_v_lineas_estado';
const INSPECCIONES = 'cables_inspecciones';
const ITEMS_LINEA = ['estado_cable', 'punto_anclaje'];

export { validarInformePdf, urlArchivo } from './arbolesService';

const amigable = (texto) => {
  const e = new Error(texto);
  e.amigable = true;
  return e;
};

const nuevoId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r % 4) + 8).toString(16);
  }));

// Lista con estado (vista), de todas las instalaciones; la pantalla filtra.
export const fetchLineasEstado = () => leerPaginado(
  () => supabase.from(VISTA).select('*').order('codigo').order('id'),
  'No se pudieron cargar los cables',
);

// Todos los eventos (también anulados; el reporte los descarta), para el
// resumen de control del reporte. Son pocos (cientos): se leen paginados y el
// filtro por período e instalación se hace en memoria.
export const fetchEventosLineas = () => leerPaginado(
  () => supabase.from(EVENTOS)
    .select('id, linea_id, fecha, tipo, responsable_nombre, responsable_profesion, empresa, certificado, resultado, detalle, recomendaciones, acciones, proxima_fecha, informe_path, anulada, created_at')
    .order('fecha', { ascending: false }).order('id'),
  'No se pudieron cargar los eventos de los cables',
);

// Ficha de un cable (de la vista: trae los códigos de sus árboles) con sus
// eventos (todos, también anulados) y el historial de cambios. El historial
// es secundario: si falla, la ficha se muestra igual y avisa.
export async function fetchLineaFicha(id) {
  const contexto = 'No se pudo cargar el cable';
  const [filas, eventos] = await Promise.all([
    leer(() => supabase.from(VISTA).select('*').eq('id', id), contexto),
    leer(() => supabase.from(EVENTOS).select('*').eq('linea_id', id)
      .order('fecha', { ascending: false }).order('created_at', { ascending: false }), 'No se pudieron cargar los eventos'),
  ]);
  const linea = filas[0] || null;
  if (!linea) return { linea: null, eventos: [], historial: [], errorHistorial: null };
  let historial = [];
  let errorHistorial = null;
  try {
    historial = await leer(() => supabase.from(HIST).select('*').eq('linea_id', id), 'No se pudo cargar el historial de cambios');
  } catch (e) {
    errorHistorial = e;
  }
  return { linea, eventos, historial, errorHistorial };
}

// Inspecciones que mencionan al cable en "Estado del cable" o "Punto de
// anclaje" (detalles.<ítem>.lineas). Una consulta por ítem; se unen por id.
export async function fetchMencionesLinea(id) {
  const listas = await Promise.all(ITEMS_LINEA.map((item) => leerPaginado(
    () => supabase.from(INSPECCIONES)
      .select('id, fecha, guia_nombre, resultados, detalles, anulada, instalacion')
      .filter(`detalles->${item}->lineas`, 'cs', JSON.stringify([id]))
      .order('fecha', { ascending: false }).order('id'),
    'No se pudieron cargar las menciones de la inspección diaria',
  )));
  const porId = new Map();
  for (const r of listas.flat()) if (r && !porId.has(r.id)) porId.set(r.id, r);
  return [...porId.values()];
}

// Cables por id (detalle de una inspección). Nunca lanza: devuelve null si
// no se pudo consultar, para mostrar "no se pudieron cargar".
export async function fetchLineasPorIds(ids) {
  const unicos = [...new Set((ids || []).filter(Boolean))];
  if (!unicos.length) return [];
  try {
    const { data, error } = await supabase.from(LINEAS)
      .select('id, codigo, nombre, plataforma_desde, plataforma_hasta, instalacion, activo').in('id', unicos);
    if (error) return null;
    return data || [];
  } catch {
    return null;
  }
}

const duplicado = (res, contexto) => {
  if (res?.error?.code === '23505') throw amigable(`${contexto}: ya existe un cable con ese código.`);
};

export async function crearLinea(datos) {
  const contexto = 'No se pudo registrar el cable';
  let res;
  try {
    res = await supabase.from(LINEAS).insert({ ...datos, activo: true }).select('*');
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  duplicado(res, contexto);
  if (res?.error) throw errorAmigable(res.error, contexto);
  const fila = (res?.data || [])[0];
  if (!fila) throw errorAmigable({ code: '42501', message: 'permission' }, contexto);
  return fila;
}

// UPDATE que RLS no deja aplicar devuelve 0 filas sin error: se pide la fila.
export async function actualizarLinea(id, cambios, contexto = 'No se pudieron guardar los cambios') {
  let res;
  try {
    res = await supabase.from(LINEAS).update(cambios).eq('id', id).select('*');
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  duplicado(res, contexto);
  if (res?.error) throw errorAmigable(res.error, contexto);
  const fila = (res?.data || [])[0];
  if (!fila) throw errorAmigable({ code: '42501', message: 'permission' }, contexto);
  return fila;
}

export async function darDeBajaLinea(id, motivo) {
  const m = exigirMotivo(motivo);
  return actualizarLinea(id, { activo: false, baja_motivo: m }, 'No se pudo dar de baja el cable');
}

async function subir(path, blob, contentType, contexto) {
  let res;
  try {
    res = await supabase.storage.from(BUCKET).upload(path, blob, { contentType, upsert: false });
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  if (res?.error) throw errorAmigable(res.error, contexto);
  return path;
}

// Foto de la ficha: se comprime, se sube a lineas/<id>/ y se guarda la ruta.
export async function subirFotoLinea(id, archivo) {
  const contexto = 'No se pudo subir la foto';
  if (!archivo || !/^image\//.test(archivo.type || '')) throw amigable(`${contexto}: elija una imagen.`);
  let blob = archivo;
  try { blob = (await compressImage(archivo)) || archivo; } catch { blob = archivo; }
  const path = await subir(`lineas/${id}/foto-${Date.now()}.jpg`, blob, 'image/jpeg', contexto);
  return actualizarLinea(id, { foto_path: path }, contexto);
}

// Evento nuevo. El id se genera aquí para que el PDF quede en
// lineas/<linea_id>/evento-<id>.pdf ANTES de insertar (el evento no se edita
// después: no se puede agregar el informe con un UPDATE).
export async function crearEvento(lineaId, datos, informe) {
  const contexto = 'No se pudo guardar el evento';
  const invalido = validarInformePdf(informe);
  if (invalido) throw amigable(`${contexto}: ${invalido}`);
  const id = nuevoId();
  let informePath = null;
  if (informe) {
    informePath = await subir(`lineas/${lineaId}/evento-${id}.pdf`, informe, 'application/pdf', 'No se pudo subir el informe');
  }
  let res;
  try {
    res = await supabase.from(EVENTOS).insert({ ...datos, id, linea_id: lineaId, informe_path: informePath }).select('*');
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  if (res?.error) throw errorAmigable(res.error, contexto);
  return (res?.data || [])[0] || { ...datos, id, linea_id: lineaId, informe_path: informePath, anulada: false };
}

export const anularEvento = (id, motivo) => anular(EVENTOS, id, motivo, 'No se pudo anular el evento');
